'use client';
import React, { useState, useEffect } from 'react';
import { useSearchParams, useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { videoLibraryData, getTopicById, VideoTopic, VideoSection } from '@/lib/videoData';

const sectionColors: Record<string, { bg: string; text: string; border: string }> = {
  'my-profile': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  'member-sign': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  'member-list': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  'group-meeting-admin': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  'events': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  'group-meeting-member': { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  'request-category': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-200' },
};

export default function VideoPlayerContent() {
  const searchParams = useSearchParams();
  const router = useRouter();
  const topicId = searchParams.get('topic') || 'edit-profile';
  const sectionId = searchParams.get('section') || 'my-profile';

  const result = getTopicById(topicId);
  const topic: VideoTopic | null = result?.topic || null;
  const section: VideoSection | null = result?.section || null;
  const colors = sectionColors[section?.id || 'my-profile'] || sectionColors['my-profile'];

  const [watchedTopics, setWatchedTopics] = useState<string[]>(['edit-profile', 'login']);

  const markWatched = (id: string) => {
    setWatchedTopics((prev) => prev.includes(id) ? prev : [...prev, id]);
  };

  useEffect(() => {
    if (topicId) markWatched(topicId);
  }, [topicId]);

  if (!topic || !section) {
    return (
      <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 py-12 text-center">
        <div className="w-16 h-16 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
          <Icon name="ExclamationTriangleIcon" size={28} className="text-muted-foreground" />
        </div>
        <h2 className="text-xl font-700 text-foreground mb-2">Tutorial Not Found</h2>
        <p className="text-muted-foreground mb-6">The requested tutorial could not be found. Please go back to the library.</p>
        <Link href="/" className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm">
          <Icon name="ArrowLeftIcon" size={16} className="text-white" />
          Back to Library
        </Link>
      </div>
    );
  }

  const currentTopicIndex = section.topics.findIndex((t) => t.id === topicId);
  const prevTopic = currentTopicIndex > 0 ? section.topics[currentTopicIndex - 1] : null;
  const nextTopic = currentTopicIndex < section.topics.length - 1 ? section.topics[currentTopicIndex + 1] : null;

  return (
    <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 2xl:px-16 py-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-5 flex-wrap">
        <Link href="/" className="hover:text-primary transition-colors flex items-center gap-1">
          <Icon name="HomeIcon" size={14} />
          <span>Library</span>
        </Link>
        <Icon name="ChevronRightIcon" size={14} />
        <Link href={`/#${section.id}`} className="hover:text-primary transition-colors">{section.label}</Link>
        <Icon name="ChevronRightIcon" size={14} />
        <span className="text-foreground font-500">{topic.label}</span>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left: Video + Info */}
        <div className="xl:col-span-2 space-y-5">
          {/* Video Player */}
          <div className="bg-card rounded-2xl border border-border overflow-hidden shadow-sm">
            {/* Video container */}
            <div className="relative bg-zinc-950 aspect-video">
              <video
                key={topic.id}
                src={topic.url}
                controls
                preload="metadata"
                className="w-full h-full"
              >
                Your browser does not support the video tag.
              </video>
            </div>

            {/* Info panel */}
            <div className="p-5">
              <div>
                <h1 className="text-xl font-700 text-foreground leading-tight">{topic.label}</h1>
                <div className="flex items-center gap-3 mt-2 flex-wrap">
                  <span className={`${colors.bg} ${colors.text} text-xs font-600 px-2.5 py-1 rounded-lg`}>
                    {section.label}
                  </span>
                  <div className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Icon name="ClockIcon" size={13} />
                    <span>{topic.duration}</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground mt-3 leading-relaxed">{topic.description}</p>
              </div>
            </div>
          </div>

          {/* Navigation: prev / next */}
          <div className="grid grid-cols-2 gap-3">
            {prevTopic ? (
              <Link
                href={`/video-player?topic=${prevTopic.id}&section=${section.id}`}
                className="bg-card border border-border rounded-xl p-4 hover:border-primary hover:bg-accent transition-all group flex items-center gap-3"
              >
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 group-hover:bg-accent">
                  <Icon name="ArrowLeftIcon" size={16} className="text-muted-foreground group-hover:text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Previous</p>
                  <p className="text-sm font-600 text-foreground truncate group-hover:text-primary">{prevTopic.label}</p>
                </div>
              </Link>
            ) : <div />}

            {nextTopic ? (
              <Link
                href={`/video-player?topic=${nextTopic.id}&section=${section.id}`}
                className="bg-card border border-border rounded-xl p-4 hover:border-primary hover:bg-accent transition-all group flex items-center gap-3 justify-end text-right"
              >
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Next</p>
                  <p className="text-sm font-600 text-foreground truncate group-hover:text-primary">{nextTopic.label}</p>
                </div>
                <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0 group-hover:bg-accent">
                  <Icon name="ArrowRightIcon" size={16} className="text-muted-foreground group-hover:text-primary" />
                </div>
              </Link>
            ) : <div />}
          </div>

          {/* All sections quick nav */}
          <div className="bg-card rounded-xl border border-border p-5">
            <h3 className="text-sm font-700 text-foreground mb-3">Browse Other Sections</h3>
            <div className="flex flex-wrap gap-2">
              {videoLibraryData.map((s) => (
                <Link
                  key={`browse-${s.id}`}
                  href={`/video-player?topic=${s.topics[0].id}&section=${s.id}`}
                  className={`text-xs font-500 px-3 py-1.5 rounded-lg border transition-all ${
                    s.id === section.id
                      ? 'bg-accent border-primary text-primary' :'bg-muted border-border text-muted-foreground hover:border-primary hover:text-primary hover:bg-accent'
                  }`}
                >
                  {s.label}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Right: Playlist */}
        <div className="xl:col-span-1">
          <div className="bg-card rounded-2xl border border-border overflow-hidden sticky top-20">
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div>
                <h3 className="font-700 text-foreground text-sm">{section.label}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{section.topics.length} tutorials in this section</p>
              </div>
              <span className={`${colors.bg} ${colors.text} text-xs font-600 px-2 py-1 rounded-lg`}>
                {currentTopicIndex + 1}/{section.topics.length}
              </span>
            </div>

            {/* Progress bar */}
            <div className="px-5 py-3 border-b border-border">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-muted-foreground">Section progress</span>
                <span className="text-xs font-600 text-primary font-tabular">
                  {Math.round((watchedTopics.filter((id) => section.topics.some((t) => t.id === id)).length / section.topics.length) * 100)}%
                </span>
              </div>
              <div className="progress-bar-bg h-1.5">
                <div
                  className="progress-bar-fill h-full"
                  style={{ width: `${(watchedTopics.filter((id) => section.topics.some((t) => t.id === id)).length / section.topics.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Playlist items */}
            <div className="divide-y divide-border max-h-[60vh] overflow-y-auto">
              {section.topics.map((t, idx) => {
                const isCurrent = t.id === topicId;
                const isWatched = watchedTopics.includes(t.id);
                return (
                  <Link
                    key={`playlist-${t.id}`}
                    href={`/video-player?topic=${t.id}&section=${section.id}`}
                    className={`flex items-center gap-3 px-4 py-3.5 transition-all group ${
                      isCurrent ? 'bg-accent' : 'hover:bg-muted/50'
                    }`}
                  >
                    {/* Index / watched indicator */}
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-700 ${
                      isCurrent
                        ? 'gradient-saffron text-white'
                        : isWatched
                        ? 'bg-green-100 text-green-700' :'bg-muted text-muted-foreground'
                    }`}>
                      {isCurrent ? (
                        <Icon name="PlayIcon" size={12} className="text-white ml-0.5" />
                      ) : isWatched ? (
                        <Icon name="CheckIcon" size={12} className="text-green-700" />
                      ) : (
                        <span>{idx + 1}</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-${isCurrent ? '700' : '500'} truncate ${isCurrent ? 'text-primary' : 'text-foreground group-hover:text-primary'}`}>
                        {t.label}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground font-tabular">{t.duration}</span>
                        {isWatched && !isCurrent && (
                          <span className="badge-watched">Watched</span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* Footer */}
            <div className="px-5 py-3 border-t border-border">
              <Link
                href="/"
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
              >
                <Icon name="ArrowLeftIcon" size={14} />
                <span>Back to full library</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}